import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDndScrolling } from 'react-dnd-scrolling';

import { AddContactAttributesModal } from 'components/contact';
import { SidebarToggleTrigger } from 'components/conversation/components/sidebar/sidebarToggleTrigger';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useConversationSidebarSettings } from 'utils/useConversationSidebarSettings';
import { useServerStorage } from 'utils/useServerStorage';

import { SuggestionCards } from './cards/KnowledgeCard';
import { CustomIntegrationCards } from './components/customIntegrationCards/customIntegrationCards';
import { CustomerInfoCards } from './components/customerInfoCards/customerInfoCards';
import {
  FlatSidebar,
  SidebarCardsSummaryData,
} from './components/flatSidebar/flatSidebar';
import { IntegrationCards } from './components/integrationCards/integrationCards';
import { SidebarCardsSummary } from './components/sidebarCardsSummary/sidebarCardsSummary';
import type {
  SidebarMode,
  SidebarSectionId,
} from './conversationSidebar.types';
import { defaultSectionOrder } from './conversationSidebar.types';
import { useCardSectionVisibility } from './hooks/useCardSectionVisibility';
import { SidebarModeContext } from './hooks/useSidebarMode';

export const ConversationSidebar = (): JSX.Element => {
  const metaConversation = useAppSelector(
    (state) => state.conversation.metaConversation,
  );
  const { hiddenItems } = useCardSectionVisibility();
  const featureFlags = useFeatureFlag([
    'sidebar-cards-admin-management',
    'conversation-right-sidebar-visibility',
  ]);
  // The admin-managed flat sidebar takes priority: whenever its flag is on we
  // show it, regardless of the legacy customize flag. Only the legacy flag →
  // legacy sectioned customization; neither → legacy sectioned sidebar.
  const showFlatSidebar = featureFlags.sidebarCardsAdminManagement;
  const legacyCustomize = featureFlags.conversationRightSidebarVisibility;
  const orgStorage = useServerStorage('organization');
  // Whether the agent may customize. On the flat path this is the effective org
  // personalization setting (defaulting to the legacy flag, matching the admin
  // settings page); on the legacy path it's the legacy flag directly.
  const canCustomize = showFlatSidebar
    ? (orgStorage.data.conversation_sidebar?.allowAgentPersonalization ??
      legacyCustomize)
    : legacyCustomize;
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('default');
  const [forceShowHidden, setForceShowHidden] = useState(false);

  // Leave customization if personalization is turned off while it's open.
  useEffect(() => {
    if (!canCustomize && sidebarMode === 'customization') {
      setSidebarMode('default');
    }
  }, [canCustomize, sidebarMode]);
  const [summary, setSummary] = useState<SidebarCardsSummaryData | null>(null);
  const { cardOpenState, isSidebarOpen, setCardOpen, toggleSidebar } =
    useConversationSidebarSettings();

  const isCardOpen = useCallback(
    (key: string) => cardOpenState[key] ?? false,
    [cardOpenState],
  );

  const toggleCard = useCallback(
    (key: string) => {
      setCardOpen(key, !(cardOpenState[key] ?? false));
    },
    [cardOpenState, setCardOpen],
  );
  const storage = useServerStorage('user');
  const sectionOrder = useMemo(
    () =>
      Array.from(
        new Set([
          ...(storage.data.conversation?.sidebarSectionOrder ?? []),
          ...defaultSectionOrder,
        ]),
      ),
    [storage.data.conversation?.sidebarSectionOrder],
  );

  const rootClassName = classnames('conversation-sidebar-holder', {
    closed: !isSidebarOpen,
  });

  // Auto-scroll the sidebar while dragging a card near its top/bottom edge.
  const rootRef = useRef<HTMLDivElement>(null);
  useDndScrolling(rootRef);

  const renderCardSection = (type: SidebarSectionId, index: number) => {
    switch (type) {
      case 'customerInfo':
        return (
          <CustomerInfoCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );

      case 'knowledge':
        return (
          <SuggestionCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
          />
        );
      case 'nativeIntegration':
        return (
          <IntegrationCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );
      case 'customIntegration':
        return (
          <CustomIntegrationCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
          />
        );
    }
  };

  // Shown centered under the header, default mode only (customization already
  // reveals every card). The component itself renders nothing when nothing is
  // hidden.
  const summaryNode =
    showFlatSidebar && summary && isSidebarOpen && sidebarMode === 'default' ? (
      <SidebarCardsSummary
        totalCount={summary.totalCount}
        hiddenCount={summary.hiddenCount}
        conditionHiddenCount={summary.conditionHiddenCount}
        isForceShown={forceShowHidden}
        onToggle={() => setForceShowHidden((prev) => !prev)}
      />
    ) : null;

  return (
    <div ref={rootRef} className={rootClassName}>
      <SidebarToggleTrigger
        isMenuOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        sidebarMode={sidebarMode}
        setSidebarMode={setSidebarMode}
        canCustomize={canCustomize}
        summary={summaryNode}
      />
      <SidebarModeContext.Provider value={sidebarMode}>
        {showFlatSidebar ? (
          <FlatSidebar
            metaConversation={metaConversation}
            isMinimized={!isSidebarOpen}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
            forceShowHidden={forceShowHidden}
            onSummaryChange={setSummary}
          />
        ) : (
          sectionOrder.map((id, index) =>
            sidebarMode === 'customization' || !hiddenItems.has(id)
              ? renderCardSection(id, index)
              : null,
          )
        )}
      </SidebarModeContext.Provider>
      <AddContactAttributesModal />
    </div>
  );
};
